import { C_YES_AND_NOT } from "@/src-refactor/modules/shared/model/const/yes-and-not.conts";
import { HGetKeyEnums } from "@rf-helper/generat-enums-from-keys";
import Yup, { array, boolean, InferType, object, string, mixed } from "@yup";

import { I_GENERIC_ITEMS } from "@/src-refactor/modules/shared/model/interface/items.interface";
import HCheckArray from "@rf-helper/check-have-value";
import { CRegexp } from "@/src-refactor/modules/shared/model/const/regexp/regexp.const";
import { C_STATUS_TRUST } from "../../../septions/property-steps/documentation-step/validations-project/validation-documents/models/trust.const";
import { C_PERTENECE } from "../../../septions/property-steps/documentation-step/validations-project/validation-documents/models/acoprivi.const";
import { IPropertyBuildingForm } from "../building-form/building.schema";

const unitsCatchmentMax80 = (building: IPropertyBuildingForm[]) => {
  if (
    !HCheckArray(building) ||
    building?.length === 0 ||
    building?.some(item => isNaN(Number(item?.unitsQuantity)))
  ) {
    return false;
  }

  const countUnits = building.reduce(
    (acc, curr) => acc + Number(curr.unitsQuantity || 0),
    0
  );

  if (isNaN(countUnits)) {
    return false;
  }
  return countUnits >= 80;
};

const getResponseHasTrust = (hasTrust: I_GENERIC_ITEMS[]) => {
  const { YES, NO, PENDING } = C_STATUS_TRUST;
  if (HCheckArray(hasTrust)) {
    return {
      YES: hasTrust[0].value === YES.value,
      NO: hasTrust[0].value === NO.value,
      PENDING: hasTrust[0].value === PENDING.value
    };
  } else {
    return {
      YES: false,
      NO: false,
      PENDING: false
    };
  }
};

const getResponseHasMeetingTrust = (hasMeetingTrust: I_GENERIC_ITEMS[]) => {
  const { YES, NOT } = C_YES_AND_NOT;
  if (HCheckArray(hasMeetingTrust)) {
    return {
      YES_MEETING: hasMeetingTrust[0].value === YES.value,
      NOT_MEETING: hasMeetingTrust[0].value === NOT.value
    };
  } else {
    return {
      YES_MEETING: false,
      NOT_MEETING: false
    };
  }
};

export const PropertyValidationDocument = {
  file_master_plan: array()
    .default([])
    .label("Plan maestro")
    .when("is_project", {
      is: (isProject: boolean) => isProject,
      then: schema => schema,
      otherwise: schema => schema
    }),
  // fideicomiso
  trust_has_trustee: array()
    .default([C_STATUS_TRUST.NO])
    .label("Tiene fideicomiso")
    .when(["is_project", "building"], {
      is: (isProject: boolean, building: IPropertyBuildingForm[]) => {
        if (!isProject || HCheckArray(building)) return false;
        return unitsCatchmentMax80(building);
      },
      then: schema => schema.min(1, "Especifique el estatus de fideicomiso"),
      otherwise: schema => schema.min(0)
    }),
  trust_name_trustee: array()
    .default([])
    .label("Nombre del fideicomiso")
    .when(
      ["is_project", "trust_has_trustee", "trust_has_meeting_trustee", "building"],
      {
        is: (
          isProject: boolean,
          trustHasTrustee: I_GENERIC_ITEMS[],
          trustHasMeetingTrustee: I_GENERIC_ITEMS[],
          building: IPropertyBuildingForm[]
        ) => {
          if (!isProject) return false;
          const { YES, PENDING } = getResponseHasTrust(trustHasTrustee);
          return (YES || PENDING) && unitsCatchmentMax80(building);
        },
        then: schema => schema.min(1, "Especifique el estatus de fideicomiso"),
        otherwise: schema => schema.min(0)
      }
    ),
  trust_name_official_trustee: string()
    .default("")
    .label("Nombre oficial del fideicomiso")
    .when(
      ["is_project", "trust_has_trustee", "trust_has_meeting_trustee", "building"],
      {
        is: (
          isProject: boolean,
          trustHasTrustee: I_GENERIC_ITEMS[],
          trustHasMeetingTrustee: I_GENERIC_ITEMS[],
          building: IPropertyBuildingForm[]
        ) => {
          if (!isProject) return false;
          const { YES, PENDING } = getResponseHasTrust(trustHasTrustee);
          return (YES || PENDING) && unitsCatchmentMax80(building);
        },
        then: schema => schema.required("Nombre oficial del fideicomiso requerido"),
        otherwise: schema => schema
      }
    ),
  trust_email_trustee: string()
    .default("")
    .label("Correo del fideicomiso")
    .when(["is_project", "trust_has_trustee", "building"], {
      is: (
        isProject: boolean,
        trustHasTrustee: I_GENERIC_ITEMS[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        const { YES, PENDING } = getResponseHasTrust(trustHasTrustee);
        return (YES || PENDING) && unitsCatchmentMax80(building);
      },
      then: schema =>
        schema
          .required("Correo del fideicomiso requerido")
          .email("Correo no válido"),
      otherwise: schema => schema
    }),
  trust_has_meeting_trustee: array()
    .default([C_YES_AND_NOT.NOT])
    .label("Tiene reunión de fideicomiso"),

  trust_entity_has_meeting_trustee: array()
    .default([])
    .label("Nombre de fiduciaria")
    .when(
      ["is_project", "trust_has_trustee", "trust_has_meeting_trustee", "building"],
      {
        is: (
          isProject: boolean,
          trustHasTrustee: I_GENERIC_ITEMS[],
          trustHasMeetingTrustee: I_GENERIC_ITEMS[],
          catchment: any[]
        ) => {
          if (!isProject) return false;
          const { YES_MEETING } = getResponseHasMeetingTrust(trustHasMeetingTrustee);
          return YES_MEETING && unitsCatchmentMax80(catchment);
        },
        then: schema =>
          schema.min(
            1,
            "Especifique el nombre de la fiduciaria, con la cual se realizó la reunión"
          ),
        otherwise: schema => schema.min(0)
      }
    ),
  trust_oficial_name_meeting_trustee: string()
    .default("")
    .label("Nombre del ejecutivo")
    .when(
      ["is_project", "trust_has_trustee", "trust_has_meeting_trustee", "catchment"],
      {
        is: (
          isProject: boolean,
          trustHasTrustee: I_GENERIC_ITEMS[],
          trustHasMeetingTrustee: I_GENERIC_ITEMS[],
          building: IPropertyBuildingForm[]
        ) => {
          if (!isProject) return false;
          const { YES_MEETING } = getResponseHasMeetingTrust(trustHasMeetingTrustee);
          return YES_MEETING && unitsCatchmentMax80(building);
        },
        then: schema =>
          schema.required(
            "Especifique el nombre del ejecutivo, con el cual se realizó la reunión"
          ),
        otherwise: schema => schema
      }
    ),

  trust_date_meeting_trustee: string()
    .default("")
    .when(
      ["is_project", "trust_has_trustee", "trust_has_meeting_trustee", "building"],
      {
        is: (
          isProject: boolean,
          trustHasTrustee: I_GENERIC_ITEMS[],
          trustHasMeetingTrustee: I_GENERIC_ITEMS[],
          building: IPropertyBuildingForm[]
        ) => {
          if (!isProject) return false;

          const { YES_MEETING } = getResponseHasMeetingTrust(trustHasMeetingTrustee);
          return YES_MEETING && unitsCatchmentMax80(building);
        },
        then: schema =>
          schema.required("Especifique la fecha de la reunión con fiduciaria"),
        otherwise: schema => schema
      }
    )
    .label("Fecha de la reunión con fiduciaria"),

  trust_notes_trustee: string().default("").label("Notas del fideicomiso"),
  trust_rnc_trustee: string()
    .default("")
    .matches(CRegexp.RNC, {
      message: "RNC no válido",
      excludeEmptyString: true
    })
    .label("RNC del fideicomiso"),
  trust_estimated_constitution_date: string()
    .default("")
    .label("Fecha estimada de constitución del fideicomiso"),
  file_definitive_trustee: array()
    .default([])
    .label("Archivo definitivo del fideicomiso"),
  date_file_definitive_trustee: string()
    .default("")
    .label("Fecha del archivo definitivo del fideicomiso")
    .when(["is_project", "file_definitive_trustee", "building"], {
      is: (
        isProject: boolean,
        fileDefinitiveTrustee: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return unitsCatchmentMax80(building) && HCheckArray(fileDefinitiveTrustee);
      },
      then: schema =>
        schema.required("Fecha de emisión de fideicomiso definitivo requerida"),
      otherwise: schema => schema.notRequired()
    }),
  file_title_transfer_trustee: array()
    .default([])
    .label("Archivo de transferencia de título del fideicomiso"),
  file_tax_receipt: array().default([]).label("Recibo de impuestos del fideicomiso"),

  // ayuntamiento
  file_deposit_in_town_hall: array()
    .default([])
    .label("Depósito en el ayuntamiento"),
  date_deposit_town_hall: string()
    .default("")
    .label("Fecha de depósito en el ayuntamiento")
    .when(["is_project", "file_deposit_in_town_hall", "building"], {
      is: (
        isProject: boolean,
        fileDepositInTownHall: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return unitsCatchmentMax80(building) && HCheckArray(fileDepositInTownHall);
      },
      then: schema => schema.required("Fecha de depósito en ayuntamiento requerida"),
      otherwise: schema => schema.notRequired()
    }),
  file_no_objection_from_town_hall: array()
    .default([])
    .label("No objeción del ayuntamiento"),
  date_no_objection_issuance: string()
    .default("")
    .label("Fecha de emisión de no objeción")
    .when(["is_project", "file_no_objection_from_town_hall", "building"], {
      is: (
        isProject: boolean,
        fileNoObjectionFromTownHall: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return (
          unitsCatchmentMax80(building) && HCheckArray(fileNoObjectionFromTownHall)
        );
      },
      then: schema => schema.required("Fecha de emisión de no objeción requerida"),
      otherwise: schema => schema.notRequired()
    }),
  // confotur
  has_confotur: array().default([C_YES_AND_NOT.YES]).label("Tiene confotur"),
  file_definitive_confotur: array()
    .default([])
    .label("Archivo definitivo de confotur"),
  date_file_definitive_confotur: string()
    .default("")
    .label("Fecha del archivo definitivo de confotur")
    .when(["is_project", "file_definitive_confotur", "building"], {
      is: (
        isProject: boolean,
        fileDefinitiveConfotur: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return unitsCatchmentMax80(building) && HCheckArray(fileDefinitiveConfotur);
      },
      then: schema =>
        schema.required("Fecha de emisión de confotur definitivo requerida"),
      otherwise: schema => schema.notRequired()
    }),
  file_previous_mitur: array().default([]).label("Archivo previo de mitur"),
  date_file_previous_mitur: string()
    .default("")
    .label("Fecha del archivo previo de mitur")
    .when(["is_project", "file_previous_mitur", "building"], {
      is: (
        isProject: boolean,
        filePreviousMitur: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return unitsCatchmentMax80(building) && HCheckArray(filePreviousMitur);
      },
      then: schema => schema.required("Fecha de emisión de mitur previo requerida"),
      otherwise: schema => schema.notRequired()
    }),
  // flujo de proyecto
  file_environment: array().default([]).label("Archivo de medio ambiente"),
  date_file_environment: string()
    .default("")
    .label("Fecha del archivo de medio ambiente")
    .when(["is_project", "file_environment", "building"], {
      is: (
        is_project: boolean,
        fileEnvironment: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!is_project) return false;
        return unitsCatchmentMax80(building) && HCheckArray(fileEnvironment);
      },
      then: schema =>
        schema.required("Fecha de emisión de medio ambiente requerida"),
      otherwise: schema => schema
    }),
  file_evidence_earth_movement: array()
    .default([])
    .label("Evidencia de movimiento de tierra"),
  date_file_evidence_earth_movement: string()
    .default("")
    .when(["is_project", "file_evidence_earth_movement", "building"], {
      is: (
        isProject: boolean,
        fileEvidenceEarthMovement: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return (
          unitsCatchmentMax80(building) && HCheckArray(fileEvidenceEarthMovement)
        );
      },
      then: schema =>
        schema.required("Fecha de emisión de movimiento de tierra requerida"),
      otherwise: schema => schema.notRequired()
    })
    .label("Fecha de evidencia de movimiento de tierra"),
  file_construction_license: array().default([]).label("Licencia de construcción"),
  date_construction_license: string()
    .default("")
    .when(["is_project", "file_construction_license", "building"], {
      is: (
        isProject: boolean,
        fileConstructionLicense: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return unitsCatchmentMax80(building) && HCheckArray(fileConstructionLicense);
      },
      then: schema =>
        schema.required("Fecha de emisión de licencia de construcción requerida"),
      otherwise: schema => schema.notRequired()
    })
    .label("Fecha de licencia de construcción"),

  // constructora desarrolladora
  file_commercial_register: array().default([]).label("Registro comercial"),
  date_file_commercial_register: string()
    .default("")
    .label("Fecha del registro comercial")
    .when(["is_project", "file_commercial_register", "building"], {
      is: (
        isProject: boolean,
        fileCommercialRegister: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return unitsCatchmentMax80(building) && HCheckArray(fileCommercialRegister);
      },
      then: schema =>
        schema.required("Fecha de emisión de el registro mercantil requerida"),
      otherwise: schema => schema
    }),
  file_construction_cv: array().default([]).label("CV de la constructora"),
  name_construction_company: string().default("").label("Nombre de la constructora"),
  file_assembly_minutes: array().default([]).label("Actas de asamblea"),
  file_due_diligence: array().default([]).label("Debida diligencia"),
  file_credit_bureau: array().default([]).label("Buró de crédito"),
  file_rnc_construction: array().default([]).label("RNC de la constructora"),
  rnc_construction: string()
    .default("")
    .matches(CRegexp.RNC, {
      message: "RNC no válido",
      excludeEmptyString: true
    })
    .label("RNC de la constructora"),

  // status property

  file_legal_status: array().default([]).label("Estatus legal"),
  date_file_legal_status: string()
    .default("")
    .when(["is_project", "file_legal_status", "building"], {
      is: (
        isProject: boolean,
        fileLegalStatus: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return unitsCatchmentMax80(building) && HCheckArray(fileLegalStatus);
      },
      then: schema => schema.required("Fecha de emisión de estatus legal requerida"),
      otherwise: schema => schema
    })
    .label("Fecha del estatus legal"),
  file_purchase_sale_land: array().default([]).label("Compra venta de terreno"),
  date_file_purchase_sale_land: string()
    .default("")
    .when(["is_project", "file_purchase_sale_land", "building"], {
      is: (
        isProject: boolean,
        filePurchaseSaleLand: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return unitsCatchmentMax80(building) && HCheckArray(filePurchaseSaleLand);
      },
      then: schema =>
        schema.required("Fecha de emisión de compra venta de terreno requerida"),
      otherwise: schema => schema
    })
    .label("Fecha de compra venta de terreno"),
  // acoprovi
  type_association_acoprovi: array()
    .default([C_PERTENECE.Acoprovi])
    .label("Tipo de asociación ACOPROVI"),
  file_acoprovi: array().default([]).label("Archivo ACOPROVI"),
  date_file_acoprovi: string()
    .default("")
    .label("Fecha del archivo ACOPROVI")
    .when(["is_project", "file_acoprovi", "building"], {
      is: (
        isProject: boolean,
        fileAcoprovi: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return unitsCatchmentMax80(building) && HCheckArray(fileAcoprovi);
      },
      then: schema =>
        schema.required("Fecha de emisión de ingreso a ACOPROVI requerida"),
      otherwise: schema => schema
    }),
  file_aprocovici: array().default([]).label("Archivo APROCOVICI"),
  date_file_aprocovici: string()
    .default("")
    .when(["is_project", "file_aprocovici", "building"], {
      is: (
        isProject: boolean,
        fileAprocovici: string[],
        building: IPropertyBuildingForm[]
      ) => {
        if (!isProject) return false;
        return unitsCatchmentMax80(building) && HCheckArray(fileAprocovici);
      },
      then: schema =>
        schema.required("Fecha de emisión de ingreso a APROCOVICI requerida"),
      otherwise: schema => schema
    })
    .label("Fecha del archivo APROCOVICI")
};

const PropertyValidationDocumentSchema = object(PropertyValidationDocument);
const CPropertyValidationDocument = PropertyValidationDocumentSchema.getDefault();
const CPropertyValidationDocumentKeys = HGetKeyEnums(
  CPropertyValidationDocument
) as {
  [K in keyof typeof CPropertyValidationDocument]: K;
};

export type IPropertyValidationDocument = InferType<
  typeof PropertyValidationDocumentSchema
>;
export {
  CPropertyValidationDocument,
  CPropertyValidationDocumentKeys,
  PropertyValidationDocumentSchema
};
